import { type ReactElement, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import type { ErrorBoundaryProps, FallbackRender } from '@sentry/react'
import { initSentry } from './sentryClient'
import { SentryErrorFallback } from './SentryErrorFallback'
import type { SentryReadyConfig } from './sentryTypes'

type FallbackProp = ErrorBoundaryProps['fallback']

type SentryProviderProps = {
  children: ReactNode
  config: SentryReadyConfig
  fallback?: FallbackProp
}

const defaultFallback: FallbackRender = (props) => (
  <SentryErrorFallback {...props} />
)

/**
 * Initializes Sentry once and wraps children in an error boundary.
 *
 * Note: `config` is read only on the first initialization — Sentry is a
 * singleton, so re-mounting this provider with a different `config` has no
 * effect after the first init.
 *
 * The default fallback (`SentryErrorFallback`) renders English, framework-
 * independent text. Pass a custom `fallback` for i18n / themed UI.
 */
export function SentryProvider({
  children,
  config,
  fallback,
}: SentryProviderProps): ReactElement {
  // initSentry is internally guarded by an `isInitialized` flag, so calling
  // it on every render is safe — it is a no-op after the first invocation.
  initSentry(config)

  return (
    <Sentry.ErrorBoundary fallback={fallback ?? defaultFallback}>
      {children}
    </Sentry.ErrorBoundary>
  )
}
